/**
 * Open projects — the sidebar's "Trabalho" section, like opening a folder in Cursor. Desktop only.
 *
 * The desktop remembers which projects are open, most recent first, across restarts
 * (`projects_list`, in workspace.rs); this module keeps the last list it saw in localStorage too, so
 * the sidebar draws at once instead of popping in. A project that is a git repository is matched to
 * the organization's linked repositories by its remote (or, without one, by its name): that is how
 * the app "already knows it" when it is opened.
 *
 * Also here: the hand-off that lets a file clicked in the sidebar open in the Editor, the way a
 * click in the Explorer does.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { isDesktop, projectClose, projectOpen, projectsList, type DesktopProject } from './desktop'
import type { OpenFile } from './files'
import type { RepositoryLink } from './types'

export type OpenProject = DesktopProject

const CACHE_KEY = 'devpulse.openProjects'
const DISMISSED_KEY = 'devpulse.openProjects.linkDismissed'
const EXPANDED_KEY = 'devpulse.openProjects.expanded'

// ------------------------------------------------------------------------------ storage

function readJson<T>(key: string, fallback: T, valid: (value: unknown) => boolean): T {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null')
    return valid(parsed) ? (parsed as T) : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode or storage blocked: it is only a head start; the desktop's list is the truth.
  }
}

const isProjectList = (value: unknown) =>
  Array.isArray(value) && value.every((p) => p && typeof p === 'object' && typeof (p as OpenProject).path === 'string' && typeof (p as OpenProject).name === 'string')
const isStringList = (value: unknown) => Array.isArray(value) && value.every((x) => typeof x === 'string')

export function cachedProjects(): OpenProject[] {
  return readJson<OpenProject[]>(CACHE_KEY, [], isProjectList)
}

export function linkOfferDismissed(path: string): boolean {
  return readJson<string[]>(DISMISSED_KEY, [], isStringList).some((p) => samePath(p, path))
}

export function dismissLinkOffer(path: string) {
  const list = readJson<string[]>(DISMISSED_KEY, [], isStringList)
  if (!list.some((p) => samePath(p, path))) writeJson(DISMISSED_KEY, [...list, path].slice(-100))
}

export function expandedProjects(): string[] {
  return readJson<string[]>(EXPANDED_KEY, [], isStringList)
}

export function setProjectExpanded(path: string, expanded: boolean) {
  const list = expandedProjects().filter((p) => !samePath(p, path))
  writeJson(EXPANDED_KEY, expanded ? [...list, path] : list)
}

// ------------------------------------------------------------------------------ pure parts

/** The same folder, however the slashes and (on Windows) the letters' case were written. */
export function samePath(a: string, b: string): boolean {
  const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '')
  const x = norm(a)
  const y = norm(b)
  // A drive letter means Windows, where case does not matter.
  return /^[a-z]:/i.test(x) ? x.toLowerCase() === y.toLowerCase() : x === y
}

/** A project opened (again): first in the list, once. */
export function upsertProject(list: OpenProject[], project: OpenProject): OpenProject[] {
  return [project, ...list.filter((p) => !samePath(p.path, project.path))]
}

export function removeProject(list: OpenProject[], path: string): OpenProject[] {
  return list.filter((p) => !samePath(p.path, path))
}

/**
 * A git remote reduced to host/owner/repo, so `git@github.com:acme/shop.git`,
 * `https://github.com/acme/shop` and `ssh://git@github.com/acme/shop.git` compare equal.
 */
export function normalizeRemote(url: string | null | undefined): string | null {
  if (!url) return null
  let value = url.trim().toLowerCase()
  if (!value) return null
  value = value.replace(/^[a-z+]+:\/\//, '') // scheme
  value = value.replace(/^[^@/]+@/, '') // user (git@, or a token that should not be there)
  value = value.replace(/^([^/:]+):(?!\d+\/)/, '$1/') // scp-like host:owner/repo
  value = value.replace(/^([^/:]+):\d+\//, '$1/') // a port
  value = value.replace(/\/+$/, '').replace(/\.git$/, '')
  return value || null
}

/** The linked repository a project is: same remote, else (no remote to compare) the same name. */
export function matchRepository(linked: RepositoryLink[], project: Pick<OpenProject, 'name' | 'remote_url'>): RepositoryLink | null {
  const remote = normalizeRemote(project.remote_url)
  if (remote) {
    const byRemote = linked.find((r) => normalizeRemote(r.remoteUrl) === remote)
    if (byRemote) return byRemote
  }
  const name = project.name.toLowerCase()
  return linked.find((r) => r.name.toLowerCase() === name && (!remote || !r.remoteUrl)) ?? null
}

// ------------------------------------------------------------------------------ the hook

export function useOpenProjects() {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: ['open-projects'],
    enabled: isDesktop(),
    placeholderData: cachedProjects,
    queryFn: async () => {
      const list = await projectsList()
      writeJson(CACHE_KEY, list)
      return list
    },
    staleTime: 60_000,
  })
  const projects = isDesktop() ? (data ?? []) : []

  const set = useCallback((change: (list: OpenProject[]) => OpenProject[]) => {
    const next = change(queryClient.getQueryData<OpenProject[]>(['open-projects']) ?? cachedProjects())
    queryClient.setQueryData(['open-projects'], next)
    writeJson(CACHE_KEY, next)
  }, [queryClient])

  /** The native dialog; the project opened, or null when cancelled. */
  const open = useCallback(async (): Promise<OpenProject | null> => {
    const project = await projectOpen()
    if (project) set((list) => upsertProject(list, project))
    return project
  }, [set])

  const close = useCallback(async (path: string) => {
    await projectClose(path)
    set((list) => removeProject(list, path))
  }, [set])

  return { projects, open, close }
}

// ------------------------------------------------------------------------------ open in the Editor

export interface OpenRequest {
  file: OpenFile
  as: 'read' | 'edit'
}

let pending: OpenRequest | null = null
const openListeners = new Set<() => void>()

/** Asks the Editor to open a file — now if it is on screen, else as soon as it is. */
export function requestOpenInEditor(file: OpenFile, as: 'read' | 'edit') {
  pending = { file, as }
  for (const listener of openListeners) listener()
}

/** The file waiting to be opened, once: taking it clears it. */
export function takePendingOpen(): OpenRequest | null {
  const request = pending
  pending = null
  return request
}

export function onOpenRequest(listener: () => void): () => void {
  openListeners.add(listener)
  return () => openListeners.delete(listener)
}
